import {
  Group,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  Shape,
  ShapeGeometry,
  Vector3,
  WebGLRenderer,
} from "three";

/**
 * Floor planes, controller hit-tests, and anchors via the WebXR APIs.
 * Replaces the unmaintained Reality Accelerator Toolkit.
 *
 * Quest scene understanding grants anchors together with plane and mesh detection.
 * Anchor creation uses the stationary "local" space, then "unbounded".
 * local-floor alone does not provide that space on current Quest OS, and restore
 * then fails with OperationError "Anchor creation failed".
 * A missing pose is skipped for that frame so a tracking hiccup does not stop the loop.
 */

export const AR_REQUIRED_FEATURES = [
  "hit-test",
  "plane-detection",
  "mesh-detection",
  "anchors",
  "local",
  "local-floor",
];

export const AR_OPTIONAL_FEATURES = ["hand-tracking", "bounded-floor", "unbounded"];

export type XRButtonOptions = {
  optionalFeatures?: string[];
  requiredFeatures?: string[];
  onSessionStarted?: (session: XRSession) => void;
  onSessionEnded?: (session: XRSession) => void;
  onSupported?: () => void;
  onUnsupported?: () => void;
  onFeaturesUnsupported?: (reason: unknown) => void;
  onNotAllowed?: (exception: unknown) => void;
  ENTER_XR_TEXT?: string;
  LEAVE_XR_TEXT?: string;
  XR_NOT_SUPPORTED_TEXT?: string;
  XR_NOT_ALLOWED_TEXT?: string;
};

export class ARButton {
  static convertToARButton(
    button: HTMLButtonElement,
    renderer: WebGLRenderer,
    options: XRButtonOptions = {},
  ): void {
    function showEnterAR(): void {
      const sessionInit: XRSessionInit = {
        requiredFeatures: options.requiredFeatures ?? [],
        optionalFeatures: options.optionalFeatures ?? [],
      };
      let currentSession: XRSession | null = null;

      async function onSessionStarted(session: XRSession): Promise<void> {
        session.addEventListener("end", onSessionEnded);
        try {
          await renderer.xr.setSession(session);
        } catch (error) {
          console.error(
            "XR session started, but the renderer could not use its reference space.",
            error,
          );
          await session.end().catch(() => undefined);
          return;
        }
        const granted = session.enabledFeatures;
        console.log(
          "AR session started.",
          granted ? `Features: ${granted.join(", ")}` : "",
        );
        button.textContent = options.LEAVE_XR_TEXT ?? "EXIT AR";
        currentSession = session;
        options.onSessionStarted?.(currentSession);
      }

      function onSessionEnded(): void {
        if (!currentSession) return;
        currentSession.removeEventListener("end", onSessionEnded);
        button.textContent = options.ENTER_XR_TEXT ?? "ENTER AR";
        options.onSessionEnded?.(currentSession);
        currentSession = null;
      }

      button.textContent = options.ENTER_XR_TEXT ?? "ENTER AR";
      button.onclick = () => {
        if (currentSession) {
          currentSession.end();
          return;
        }
        navigator.xr
          ?.requestSession("immersive-ar", sessionInit)
          .then(onSessionStarted)
          .catch((reason) => {
            console.error("Immersive AR session was rejected.", reason);
            options.onFeaturesUnsupported?.(reason);
          });
      };
      options.onSupported?.();
    }

    function showARNotSupported(): void {
      button.onclick = null;
      button.classList.add("ar-not-supported");
      button.textContent = options.XR_NOT_SUPPORTED_TEXT ?? "AR NOT SUPPORTED";
      options.onUnsupported?.();
    }

    function showARNotAllowed(exception: unknown): void {
      button.onclick = null;
      button.classList.add("ar-not-allowed");
      button.textContent = options.XR_NOT_ALLOWED_TEXT ?? "AR NOT ALLOWED";
      console.warn("Exception when trying to call xr.isSessionSupported", exception);
      options.onNotAllowed?.(exception);
    }

    if (!navigator.xr) {
      showARNotSupported();
      return;
    }
    navigator.xr
      .isSessionSupported("immersive-ar")
      .then((supported) => {
        if (supported) showEnterAR();
        else showARNotSupported();
      })
      .catch(showARNotAllowed);
  }
}

export class FloorPlane extends Group {
  planeMesh!: Mesh;
  lastUpdated = -Infinity;

  constructor(readonly xrPlane: XRPlane) {
    super();
  }
}

export class PlacementAnchor extends Group {
  anchorID?: string;

  constructor(
    readonly xrAnchor: XRAnchor,
    anchorID?: string,
  ) {
    super();
    this.anchorID = anchorID;
  }

  get isPersistent(): boolean {
    return this.anchorID != null;
  }
}

export class HitTestTarget extends Group {
  hitTestResultValid = false;

  constructor(readonly xrHitTestSource: XRHitTestSource) {
    super();
  }
}

const poseMatrix = new Matrix4();
const poseScale = new Vector3();

export class SceneUnderstanding {
  readonly root = new Group();
  readonly planes = new Set<FloorPlane>();
  readonly anchors = new Set<PlacementAnchor>();
  readonly hitTestTargets = new Set<HitTestTarget>();

  onPlaneAdded?: (plane: FloorPlane) => void;
  onPlaneRemoved?: (plane: FloorPlane) => void;

  private session: XRSession | undefined;
  private readonly planeByXr = new Map<XRPlane, FloorPlane>();
  private warnedMissingPlanes = false;
  private warnedHitTest = false;
  private roomCaptureRequested = false;
  private anchorSpacesReady: Promise<void> | null = null;

  constructor(
    private readonly manager: {
      isPresenting: boolean;
      getFrame(): XRFrame | null;
      getSession(): XRSession | null;
      getReferenceSpace(): XRReferenceSpace | null;
    },
  ) {}

  update(): void {
    if (!this.manager.isPresenting) {
      if (this.session) {
        this.session = undefined;
        this.clearTrackedObjects();
      }
      return;
    }

    const frame = this.manager.getFrame();
    const session = this.manager.getSession();
    const referenceSpace = this.manager.getReferenceSpace();
    if (!frame || !session || !referenceSpace) return;

    if (session !== this.session) {
      this.clearTrackedObjects();
      this.session = session;
      this.warnedMissingPlanes = false;
      this.warnedHitTest = false;
      this.roomCaptureRequested = false;
      this.anchorSpacesReady = null;
      void this.ensureAnchorSpaces(session);
    }

    this.syncPlanes(frame, referenceSpace);
    this.anchors.forEach((anchor) => {
      this.applySpacePose(anchor, anchor.xrAnchor.anchorSpace, frame, referenceSpace);
    });
    this.hitTestTargets.forEach((target) => {
      this.updateHitTestTarget(target, frame, referenceSpace);
    });
  }

  async createAnchor(
    position: Vector3,
    quaternion: Quaternion,
    persistent = false,
  ): Promise<PlacementAnchor> {
    const frame = this.manager.getFrame();
    const referenceSpace = this.manager.getReferenceSpace();
    const session = this.manager.getSession();
    if (session) void this.ensureAnchorSpaces(session);
    if (!frame?.createAnchor) {
      throw new Error("XRFrame.createAnchor is not available outside the XR animation frame.");
    }
    if (!referenceSpace) {
      throw new Error("XR reference space is not available yet.");
    }

    const pose = new XRRigidTransform(
      { x: position.x, y: position.y, z: position.z, w: 1 },
      { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w },
    );
    const xrAnchor = await frame.createAnchor(pose, referenceSpace);
    if (!xrAnchor) {
      throw new Error("XRAnchor creation failed");
    }

    const anchor = new PlacementAnchor(xrAnchor);
    this.root.add(anchor);
    this.anchors.add(anchor);

    if (persistent) {
      if (!xrAnchor.requestPersistentHandle) {
        console.warn("This session cannot save anchors. The placement will last until you exit AR.");
      } else {
        try {
          const handle = await xrAnchor.requestPersistentHandle();
          if (handle) anchor.anchorID = handle;
        } catch (error) {
          console.warn(
            "Anchor created for this session, but it could not be saved.",
            error,
          );
        }
      }
    }

    return anchor;
  }

  async deleteAnchor(anchor: PlacementAnchor | null): Promise<void> {
    if (!anchor) return;
    this.anchors.delete(anchor);
    this.root.remove(anchor);
    // Quest cannot remove a persistent entry. deletePersistentAnchor replaces a
    // real id with "", and deleting that empty id fails, so the list only grows.
    if (anchor.isPersistent) return;

    try {
      anchor.xrAnchor.delete();
    } catch (error) {
      console.warn("Could not delete the anchor.", error);
    }
  }

  async restorePersistentAnchors(): Promise<void> {
    const session = this.manager.getSession();
    if (!session?.restorePersistentAnchor) {
      console.warn("This session cannot restore saved anchors.");
      return;
    }

    let ids: readonly string[];
    try {
      ids = session.persistentAnchors ?? [];
    } catch (error) {
      console.warn("Persistent anchors are not available in this session.", error);
      return;
    }

    console.log(
      "Session reports persistent anchors:",
      ids.map((id) => id || "(empty)"),
    );
    if (ids.length === 0) return;

    // Restore goes through the same native create path as a new anchor, and that
    // path needs the local space to exist before it is called.
    await this.ensureAnchorSpaces(session);

    // Empty strings are leftover slots. They cannot be deleted, so leave them
    // alone and restore only the newest real id.
    const validIds = ids.filter((id) => id.length > 0);
    const keepId = validIds.length > 0 ? validIds[validIds.length - 1] : null;
    if (!keepId) return;

    await this.captureRoomIfEmpty(session);
    await this.restoreOneAnchor(session, keepId);
  }

  private async restoreOneAnchor(session: XRSession, id: string): Promise<boolean> {
    if (!session.restorePersistentAnchor) return false;
    try {
      const xrAnchor = await session.restorePersistentAnchor(id);
      if (!xrAnchor) {
        console.log("No anchor found for", id);
        return false;
      }
      const anchor = new PlacementAnchor(xrAnchor, id);
      this.root.add(anchor);
      this.anchors.add(anchor);
      console.log("Restored previous anchor", id);
      return true;
    } catch (error) {
      console.warn("Failed to restore saved anchor", id, error);
      return false;
    }
  }

  private ensureAnchorSpaces(session: XRSession): Promise<void> {
    if (this.anchorSpacesReady) return this.anchorSpacesReady;
    const granted = session.enabledFeatures ? Array.from(session.enabledFeatures) : null;
    this.anchorSpacesReady = (async () => {
      await this.requestSpace(session, "local");
      if (!granted || granted.includes("bounded-floor")) {
        await this.requestSpace(session, "bounded-floor");
      }
      if (!granted || granted.includes("unbounded")) {
        await this.requestSpace(session, "unbounded");
      }
    })();
    return this.anchorSpacesReady;
  }

  private async requestSpace(session: XRSession, type: XRReferenceSpaceType): Promise<void> {
    try {
      await session.requestReferenceSpace(type);
    } catch (error) {
      if (type === "local") {
        console.warn("The local reference space anchors need is not available.", error);
      }
    }
  }

  private async captureRoomIfEmpty(session: XRSession): Promise<void> {
    if (this.planes.size > 0 || this.roomCaptureRequested || !session.initiateRoomCapture) return;
    this.roomCaptureRequested = true;
    console.log("No room planes yet. Opening room capture so anchors have a space to attach to.");
    try {
      await session.initiateRoomCapture();
    } catch (error) {
      console.warn("Room capture did not start.", error);
    }
  }

  async createHitTestTargetFromControllerSpace(handedness: string): Promise<HitTestTarget> {
    const session = this.manager.getSession();
    if (!session) {
      throw new Error("No XR session");
    }
    const source = Array.from(session.inputSources).find(
      (input) => input.handedness === handedness && input.targetRaySpace,
    );
    if (!source) {
      throw new DOMException("requested XRInputSource cannot be found", "NotFoundError");
    }
    return this.createHitTestTargetFromSpace(source.targetRaySpace);
  }

  deleteHitTestTarget(target: HitTestTarget | null): void {
    if (!target) return;
    try {
      target.xrHitTestSource.cancel();
    } catch (error) {
      console.warn("Could not cancel hit-test source.", error);
    }
    this.root.remove(target);
    this.hitTestTargets.delete(target);
  }

  private async createHitTestTargetFromSpace(space: XRSpace): Promise<HitTestTarget> {
    const session = this.manager.getSession();
    if (!session?.requestHitTestSource) {
      throw new Error("session.requestHitTestSource is not available");
    }
    const xrHitTestSource = await session.requestHitTestSource({
      space,
      offsetRay: new XRRay(
        { x: 0, y: 0, z: 0, w: 1 },
        { x: 0, y: 0, z: -1, w: 0 },
      ),
    });
    if (!xrHitTestSource) {
      throw new Error("XRHitTestSource request failed");
    }
    const target = new HitTestTarget(xrHitTestSource);
    this.root.add(target);
    this.hitTestTargets.add(target);
    return target;
  }

  private syncPlanes(frame: XRFrame, referenceSpace: XRReferenceSpace): void {
    const detected = this.readDetectedPlanes(frame);
    if (!detected) {
      if (!this.warnedMissingPlanes) {
        this.warnedMissingPlanes = true;
        console.warn("This session is not reporting detected planes.");
      }
      return;
    }

    const seen = new Set<XRPlane>();
    detected.forEach((xrPlane) => {
      seen.add(xrPlane);
      const existing = this.planeByXr.get(xrPlane);
      if (!existing) {
        const plane = new FloorPlane(xrPlane);
        this.updatePlane(plane, frame, referenceSpace);
        if (!plane.planeMesh) return;
        this.planeByXr.set(xrPlane, plane);
        this.planes.add(plane);
        this.root.add(plane);
        // console.log("Plane detected", xrPlane.orientation ?? "");
        this.onPlaneAdded?.(plane);
        return;
      }
      this.updatePlane(existing, frame, referenceSpace);
    });

    for (const plane of Array.from(this.planes)) {
      if (seen.has(plane.xrPlane)) continue;
      this.planes.delete(plane);
      this.planeByXr.delete(plane.xrPlane);
      this.root.remove(plane);
      plane.planeMesh?.geometry.dispose();
      this.onPlaneRemoved?.(plane);
    }
  }

  private updatePlane(plane: FloorPlane, frame: XRFrame, referenceSpace: XRReferenceSpace): void {
    this.applySpacePose(plane, plane.xrPlane.planeSpace, frame, referenceSpace);
    if (plane.xrPlane.lastChangedTime <= plane.lastUpdated) return;
    if (plane.xrPlane.polygon.length < 3) return;

    const geometry = createGeometryFromPolygon(plane.xrPlane.polygon);
    if (plane.planeMesh) {
      plane.planeMesh.geometry.dispose();
      plane.planeMesh.geometry = geometry;
    } else {
      plane.planeMesh = new Mesh(geometry, new MeshBasicMaterial());
      plane.add(plane.planeMesh);
    }
    plane.lastUpdated = plane.xrPlane.lastChangedTime;
  }

  private updateHitTestTarget(
    target: HitTestTarget,
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
  ): void {
    let results: XRHitTestResult[] = [];
    try {
      results = frame.getHitTestResults(target.xrHitTestSource);
    } catch (error) {
      target.hitTestResultValid = false;
      if (!this.warnedHitTest) {
        this.warnedHitTest = true;
        console.warn("Hit-test results are not available.", error);
      }
      return;
    }
    target.hitTestResultValid = false;
    if (results.length === 0) return;

    const hitPose = results[0].getPose(referenceSpace);
    if (!hitPose) return;

    const position = hitPose.transform.position;
    const orientation = hitPose.transform.orientation;
    target.position.set(position.x, position.y, position.z);
    target.quaternion.set(orientation.x, orientation.y, orientation.z, orientation.w);
    target.hitTestResultValid = true;
  }

  private applySpacePose(
    object: Group,
    space: XRSpace,
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
  ): boolean {
    const pose = frame.getPose(space, referenceSpace);
    if (!pose) return false;
    poseMatrix.fromArray(pose.transform.matrix).decompose(object.position, object.quaternion, poseScale);
    return true;
  }

  private readDetectedPlanes(frame: XRFrame): XRPlaneSet | undefined {
    try {
      return frame.detectedPlanes;
    } catch {
      return undefined;
    }
  }

  private clearTrackedObjects(): void {
    for (const plane of Array.from(this.planes)) {
      this.planes.delete(plane);
      this.planeByXr.delete(plane.xrPlane);
      this.root.remove(plane);
      plane.planeMesh?.geometry.dispose();
      this.onPlaneRemoved?.(plane);
    }
    for (const anchor of Array.from(this.anchors)) {
      this.anchors.delete(anchor);
      this.root.remove(anchor);
    }
    for (const target of Array.from(this.hitTestTargets)) {
      try {
        target.xrHitTestSource.cancel();
      } catch {
        // The session has already invalidated the source.
      }
      this.hitTestTargets.delete(target);
      this.root.remove(target);
    }
  }
}

function createGeometryFromPolygon(polygon: readonly DOMPointReadOnly[]): ShapeGeometry {
  const shape = new Shape();
  polygon.forEach((point, index) => {
    if (index === 0) shape.moveTo(point.x, point.z);
    else shape.lineTo(point.x, point.z);
  });
  const geometry = new ShapeGeometry(shape);
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}
